import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const MediaContext = createContext();

export const useMedia = () => {
  const context = useContext(MediaContext);
  if (!context) {
    throw new Error('useMedia must be used within a MediaProvider');
  }
  return context;
};

export const MediaProvider = ({ children }) => {
  const [mediaSettings, setMediaSettings] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchMediaSettings = useCallback(async () => {
    try {
      const response = await fetch(`/api/media-api?t=${Date.now()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        cache: 'no-store'
      });

      if (response.ok) {
        const data = await response.json();
        const settings = {};
        data.forEach(setting => {
          settings[setting.section] = setting;
        });
        setMediaSettings(settings);
      }
    } catch (error) {
      // Silently fail - media settings are not critical
    } finally {
      setLoading(false);
    }
  }, []);

  const getMediaUrl = useCallback((sectionId, type = 'image') => {
    const setting = mediaSettings[sectionId];
    if (setting) {
      return type === 'image' ? setting.current_image : setting.current_video;
    }
    return null;
  }, [mediaSettings]);

  useEffect(() => {
    fetchMediaSettings();

    const handleFocus = () => {
      fetchMediaSettings();
    };

    const interval = setInterval(fetchMediaSettings, 300000);

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
      clearInterval(interval);
    };
  }, [fetchMediaSettings]);

  const value = useMemo(() => ({
    mediaSettings,
    loading,
    getMediaUrl,
    fetchMediaSettings,
    refreshMedia: fetchMediaSettings
  }), [mediaSettings, loading, getMediaUrl, fetchMediaSettings]);

  return (
    <MediaContext.Provider value={value}>
      {children}
    </MediaContext.Provider>
  );
}; 